import { useConsumerWithBillData } from "@features/cx/consumerManagement/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Card, CardHeader, CardTitle, CardContent } from "@shared/ui/card";
import {
  Heart,
  AlertTriangle,
  MessageSquare,
  Settings,
  User,
  Users,
} from "lucide-react";
import { useParams } from "react-router-dom";
import { formatTerritoryAddress } from "@shared/utils/address";

interface RelationshipInsightsProps {
  accountAge: number;
  serviceRequestCount: number;
  complaintCount: number;
  communicationPreference: string;
  consumer?: any;
}

export function RelationshipInsights({
  accountAge,
  serviceRequestCount,
  complaintCount,
  communicationPreference,
  consumer,
}: RelationshipInsightsProps) {
  const { consumerNo } = useParams<{ consumerNo: string }>();
  const remoteUtilityId = useRemoteUtilityId();

  const {
    data: consumerInfo,
    isLoading: loading,
    error,
    refetch: fetchConsumerData,
  } = useConsumerWithBillData(consumerNo!, remoteUtilityId);

  const currentConsumer = consumerInfo || consumer;

  const serviceAddress =
    formatTerritoryAddress(currentConsumer?.territoryData, "service") || "NA";
  const billingAddress =
    formatTerritoryAddress(currentConsumer?.territoryData, "billing") || "NA";
  const isBillingAccount =
    currentConsumer?.isBillingAccount === true ||
    currentConsumer?.isBillingAccount === "true" ||
    currentConsumer?.is_billing_account === true ||
    currentConsumer?.is_billing_account === "true"
      ? "Yes"
      : "No";

  const quantityMapping =
    currentConsumer?.additionalData?.utilityServiceQuantityMapping || {};

  const rawServices =
    currentConsumer?.utilityService ||
    (currentConsumer as any)?.selectServicesHeader ||
    [];
  let servicesArray = Array.isArray(rawServices)
    ? rawServices
    : rawServices
      ? [rawServices]
      : [];

  if (
    servicesArray.length === 0 &&
    quantityMapping &&
    typeof quantityMapping === "object"
  ) {
    servicesArray = Object.keys(quantityMapping);
  }

  const getQuantityCount = (serviceName: string) => {
    if (
      !serviceName ||
      !quantityMapping ||
      typeof quantityMapping !== "object"
    ) {
      return undefined;
    }
    if (quantityMapping[serviceName] !== undefined) {
      return quantityMapping[serviceName];
    }
    const target = String(serviceName).toLowerCase();
    const matchedKey = Object.keys(quantityMapping).find(
      (key) => key.toLowerCase() === target,
    );
    if (matchedKey && quantityMapping[matchedKey] !== undefined) {
      return quantityMapping[matchedKey];
    }
    return undefined;
  };

  return (
    <div className="space-y-4">
      {/* Service Details Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-blue-500" />
            Service Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-6">
            <div>
              <div className="card-subheading-text">Service Start Date</div>
              <div className="text-base text-black">
                {currentConsumer?.activateDate ?? "NA"}
              </div>
            </div>
            <div>
              <div className="card-subheading-text">Utility Service</div>
              <div className="text-base text-black">
                {Array.isArray(currentConsumer?.utilityService)
                  ? currentConsumer.utilityService.join(", ")
                  : currentConsumer?.utilityService || "NA"}
              </div>
            </div>
            <div>
              <div className="card-subheading-text">
                Subscribed for paper billing
              </div>
              <div className="text-base text-black">
                {currentConsumer?.isPaperedBill ? "Yes" : "No"}
              </div>
            </div>
            <div>
              <div className="card-subheading-text">Account Age</div>
              <div className="text-base text-black">{accountAge} months</div>
            </div>
            <div>
              <div className="card-subheading-text">Billing Account</div>
              <div className="text-base text-black">{isBillingAccount}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-100">
            <div>
              <div className="card-subheading-text mb-1">Service Address</div>
              <div className="text-base text-black font-normal whitespace-pre-line">
                {serviceAddress}
              </div>
            </div>
            <div>
              <div className="card-subheading-text mb-1">Billing Address</div>
              <div className="text-base text-black font-normal whitespace-pre-line">
                {billingAddress}
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <div className="text-xs font-semibold text-gray-500 tracking-wider mb-3">
              Number of Connections
            </div>
            <div className="flex flex-col gap-3">
              {(() => {
                const visibleServices = servicesArray
                  .map((service: any) => {
                    const srvName =
                      typeof service === "object" && service !== null
                        ? service.value || service.label
                        : service;
                    const label =
                      typeof service === "object" && service !== null
                        ? service.label || service.value
                        : service;
                    const count =
                      getQuantityCount(srvName) ?? getQuantityCount(label) ?? 0;
                    return {
                      label: String(label || srvName || ""),
                      count: Number(count) || 0,
                    };
                  })
                  .filter((item: any) => item.count > 0);

                if (visibleServices.length === 0) {
                  return (
                    <p className="text-sm text-muted-foreground italic">
                      No utility services selected
                    </p>
                  );
                }

                return visibleServices.map((item: any, idx: number) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3.5 px-5 rounded-xl border border-gray-200 bg-gray-50"
                  >
                    <span className="text-sm font-medium text-gray-800">
                      {item.label}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-base font-bold text-gray-900">
                        {item.count}
                      </span>
                      <span className="text-xs text-gray-500 font-normal">
                        {item.count === 1 ? "connection" : "connections"}
                      </span>
                    </div>
                  </div>
                ));
              })()}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Owner Details Card */}
      {consumerInfo?.ownershipDisplay === "Tenant" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-blue-500" />
              Owner Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <div className="card-subheading-text">Name</div>
                  <div className="text-base text-black">
                    {(consumerInfo as any)?.ownershipDetails?.ownershipDetail
                      ?.ownerName || "-"}
                  </div>
                </div>

                <div>
                  <div className="card-subheading-text">Phone</div>
                  <div className="text-base text-black">
                    {(consumerInfo as any)?.ownershipDetails?.ownershipDetail
                      ?.ownerPhone || "-"}
                  </div>
                </div>
                <div>
                  <div className="card-subheading-text">Email</div>
                  <div className="text-base text-black">
                    {(consumerInfo as any)?.ownershipDetails?.ownershipDetail
                      ?.ownerEmail || "-"}
                  </div>
                </div>

                <div>
                  <div className="card-subheading-text">Owner Type</div>
                  <div className="text-base text-black">
                    {(consumerInfo as any)?.ownershipDetails?.ownershipDetail
                      ?.ownerType || "Primary Owner"}
                  </div>
                </div>

                <div className="col-span-1 sm:col-span-2 md:col-span-4">
                  <div className="card-subheading-text">Address</div>
                  <div className="text-base text-black">
                    {(consumerInfo as any)?.ownershipDetails?.ownershipDetail
                      ?.ownerAddress || "-"}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Master Account Details Card */}
      {consumerInfo?.ownershipDisplay === "Sub-Account" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-500" />
              Master Account Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <div className="card-subheading-text">Account No</div>
                  <div className="text-base text-black">
                    {(consumerInfo as any)?.ownershipDetails?.approvalDetails
                      ?.masterAccountNo || "-"}
                  </div>
                </div>

                <div>
                  <div className="card-subheading-text">Name</div>
                  <div className="text-base text-black">
                    {(consumerInfo as any)?.ownershipDetails?.approvalDetails
                      ?.masterAccountName || "-"}
                  </div>
                </div>
                <div>
                  <div className="card-subheading-text">Phone Number</div>
                  <div className="text-base text-black">
                    {(consumerInfo as any)?.ownershipDetails
                      ?.masterAccountContactNo || "-"}
                  </div>
                </div>

                <div>
                  <div className="card-subheading-text">Email</div>
                  <div className="truncate text-base text-black">
                    {(consumerInfo as any)?.ownershipDetails
                      ?.masterAccountEmail || "-"}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Co Applicant Details Card */}
      {consumerInfo?.additionalData?.secondaryConsumer?.firstName && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-blue-500" />
              Co Applicant Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <div className="card-subheading-text">Name</div>
                  <div className="text-base text-black">
                    {consumerInfo?.additionalData?.secondaryConsumer?.firstName}{" "}
                    {consumerInfo?.additionalData?.secondaryConsumer?.lastName}
                  </div>
                </div>

                <div>
                  <div className="card-subheading-text">Email</div>
                  <div className="text-base text-black">
                    {consumerInfo?.additionalData?.secondaryConsumer?.email}
                  </div>
                </div>

                <div>
                  <div className="card-subheading-text">Phone Number</div>
                  <div className="text-base text-black">
                    {
                      consumerInfo?.additionalData?.secondaryConsumer
                        ?.contactNumber
                    }
                  </div>
                </div>

                <div>
                  <div className="card-subheading-text">
                    Relation to Applicant
                  </div>
                  <div className="text-base text-black">
                    {consumerInfo?.additionalData?.secondaryConsumer
                      ?.secondaryConsumerDisplay ?? "NA"}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
